import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';
import DateAdapter from '@mui/lab/AdapterMoment';
import { IHeader } from 'components/Header';
import Header from 'components/Header/Header';
import { useLocation, useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { IReducer } from 'Reducers/IReducer';
import { IFormikPersistSample, IPersistSampleFoodAndWaterProps } from 'pages/Sample/PersistSampleFoodAndWater';
import { searchCustomer } from 'actions/CustomerActions';
import {
  searchMasterList,
  searchTestGroupList,
  createSample,
  getSampleById,
  updateSample,
  searchReference,
  copySample,
} from 'actions/LaboratoryActions';
import { getUserList } from 'actions/UserAction';
import { SearchDialog } from 'components/SearchDialog';
import { ICustomer, IMaster, IReference, ITestGroup, IUser } from 'exactt-types/lib';
import { useFormik } from 'formik';
import useQuery from 'utils/getQueryParams';
import moment from 'moment';
import { CustomFieldWithLabel } from 'components/CustomFieldWithLabel';
import { createSampleSchema } from 'FormSchema/CreateSampleSchema';
import { handleChangeWhiteSpace } from 'utils/removeWhiteSpaces';
import { normalizeDateForApi } from 'utils/date';
import AsyncSelect from 'react-select/async';
import ReactSelect from 'react-select';
import makeAnimated from 'react-select/animated';

const animatedComponents = makeAnimated();

const getEntityId = (entity: any) => entity?._id || entity?.id || (typeof entity === 'string' ? entity : '');

const buildCustomerLabel = (customer: any) => {
  if (!customer) return '';
  const name = customer.name || customer.customerName || customer.label || '';
  const address = customer.address?.[0];
  const addressText = [address?.line1, address?.line2, address?.city, address?.state].filter(Boolean).join(', ');
  return addressText ? `${name} (${addressText})` : name;
};

export default function _PersistSampleFoodAndWater(props: IPersistSampleFoodAndWaterProps) {
  const navigate = useNavigate();
  const location = useLocation();
  let query = useQuery();
  let sampleId = query.get('id');
  const copySampleData = (location.state as any)?.copySampleData;
  const laboratoryId = props.laboratory?.details?._id || '';
  const isCopyMode = Boolean(copySampleData) && !sampleId;

  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchModal, setCustomerSearchModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null);
  const [referenceSearchQuery, setReferenceSearchQuery] = useState('');
  const [referenceSearchModal, setReferenceSearchModal] = useState(false);
  const [selectedReference, setSelectedReference] = useState<IReference | null>(null);
  const [masterSearchQuery, setMasterSearchQuery] = useState('');
  const [masterSearchModal, setMasterSearchModal] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState<IMaster | null>(null);
  const [availableParameters, setAvailableParameters] = useState<any[]>([]);
  const [testGroupSearchQuery, setTestGroupSearchQuery] = useState('');
  const [testGroupSearchModal, setTestGroupSearchModal] = useState(false);
  const [selectedTestGroup, setSelectedTestGroup] = useState<ITestGroup | null>(null);

  const formik: IFormikPersistSample = useFormik({
    initialValues: createSampleSchema.initialValues,
    validationSchema: createSampleSchema.validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const samplePayload = {
        ...values,
        collectionDate: normalizeDateForApi(values.collectionDate) || values.collectionDate,
        sampleCollectionDate: normalizeDateForApi((values as any).sampleCollectionDate),
      };

      try {
        if (sampleId) {
          await props.updateSample({
            ...samplePayload,
            laboratoryId,
            _id: sampleId,
            master: selectedMaster?._id || '',
            formType: 'WATER',
            sampleReceivedBy: '',
            dispatchMode: '',
          } as any);
          navigate('/laboratory/samples/sampleDetails?id=' + sampleId);
        } else {
          await props.createSample({
            ...samplePayload,
            laboratoryId,
            master: selectedMaster?._id || '',
            formType: 'WATER',
            sampleReceivedBy: '',
            dispatchMode: '',
          } as any);
          navigate('/laboratory/samples');
        }
      } catch (err) {
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (sampleId) {
      props.getSampleById(laboratoryId, sampleId);
    }
    // eslint-disable-next-line
  }, [sampleId, laboratoryId]);
  useEffect(() => {
    const sourceSample: any = sampleId ? props.sampleDetails : copySampleData;
    const hasSourceSample = Boolean(copySampleData || sourceSample?._id || sourceSample?.customer);
    if (hasSourceSample) {
      const normalizedParameters =
        sourceSample.parameters?.map((i: any) => ({
          ...i,
          department: i.department && typeof i.department === 'object' ? i.department._id : i.department || '',
        })) || [];

      setSelectedCustomer(
        typeof sourceSample.customer === 'object'
          ? (sourceSample.customer as ICustomer)
          : ({
            _id: getEntityId(sourceSample.customer),
            name: sourceSample.customerName || sourceSample.bookingParty || '',
          } as ICustomer)
      );
      setSelectedTestGroup(sourceSample.testGroup as ITestGroup);
      setSelectedMaster(sourceSample.master as IMaster);
      setAvailableParameters((sourceSample.master as any)?.parameters?.length ? (sourceSample.master as any).parameters : normalizedParameters);

      formik.setValues({
        ...createSampleSchema.initialValues,
        // !dates are set to new Date() because of the error in date format
        testAsPer: sourceSample.testAsPer || '',
        remark1: sourceSample.remark1 || '',
        specialInstructions: sourceSample.specialInstructions || '',
        instructionForUsed: sourceSample.instructionForUsed || '',
        countryOfOrigin: sourceSample.countryOfOrigin || '',
        purposeOfMonitoring: sourceSample.purposeOfMonitoring || '',
        emissionSourceMonitor: sourceSample.emissionSourceMonitor || '',
        productManufacturedSamplingTime: sourceSample.productManufacturedSamplingTime || '',
        nameOfPlantSection: sourceSample.nameOfPlantSection || '',
        controlMeasures: sourceSample.controlMeasures || '',
        authorizedPersonAddress: sourceSample.authorizedPersonAddress || '',
        recoveryOfMaterial: sourceSample.recoveryOfMaterial || '',
        sealOnContainerBISSeal: sourceSample.sealOnContainerBISSeal || '',
        generalSensory: sourceSample.generalSensory || '',
        CHSRegnNo: sourceSample.CHSRegnNo || '',
        fugitiveEmission: sourceSample.fugitiveEmission || '',
        CHACardHolder: sourceSample.CHACardHolder || '',
        stackTemp: sourceSample.stackTemp || '',
        dateAndPlaceOfCollectionOfSample: sourceSample.dateAndPlaceOfCollectionOfSample || '',
        ambientTemperature: sourceSample.ambientTemperature || '',
        batchNo: sourceSample.batchNo || '',
        testMethod: sourceSample.testMethod || '',
        nameAndAddressOfImporter: sourceSample.nameAndAddressOfImporter || '',
        refNo: sourceSample.refNo || '',
        locationOfAnalysis: sourceSample.locationOfAnalysis || '',
        sourceOfWater: sourceSample.sourceOfWater || '',
        statement: sourceSample.statement || '',
        batchSize: sourceSample.batchSize || '',
        brandName: sourceSample.brandName || '',
        authorizedSignature1: (sourceSample.authorizedSignature1 as IUser)?._id || '',
        collectionBy: (sourceSample.collectionBy as any) || '',
        collectionDate: normalizeDateForApi(sourceSample.collectionDate) || new Date(),
        sampleCollectionDate: normalizeDateForApi(sourceSample.sampleCollectionDate),
        description: sourceSample.description || '',
        discount: sourceSample.discount || 0,
        dueDate: sourceSample.dueDate || new Date(),
        labDueDate: sourceSample.labDueDate || new Date(),
        drugLicNo: sourceSample.drugLicNo || '',
        envCondition: sourceSample.envCondition || '',
        sampleReceivedMode: sourceSample.sampleReceivedMode || '',
        otherInformation: sourceSample.otherInformation || '',
        expDate: sourceSample.expDate || null,
        gst: sourceSample.gst || 0,
        hod: (sourceSample.hod as IUser)?._id || '',
        igst: sourceSample.igst || false,
        mfgDate: sourceSample.mfgDate || null,
        name: sourceSample.name,
        paidAmount: sourceSample.paidAmount || 0,
        parameters: normalizedParameters,
        rate: sourceSample.rate || 0,
        sac: sourceSample.sac || '',
        sampleCode: sourceSample.sampleCode || '',
        sampleCondition: sourceSample.sampleCondition || '',
        samplePacking: sourceSample.samplePacking || '',
        sampleQty: sourceSample.sampleQty || '',
        samplingMethod: sourceSample.samplingMethod || '',
        servingSize: sourceSample.servingSize || '',
        supplier: sourceSample.supplier || '',
        testGroup: getEntityId(sourceSample.testGroup) || undefined,
        type: sourceSample.type || '',
        additionalFields: sourceSample.additionalFields || {},
        customer: getEntityId(sourceSample.customer),
        reference: getEntityId(sourceSample.reference),
        genericName: sourceSample.genericName,
        manufacture: sourceSample.manufacture || '',
        marking: sourceSample.marking || '',
        paymentDueDate: sourceSample.paymentDueDate || new Date(),
        reviewedBy: (sourceSample.reviewedBy as any)?._id || '',
        authorizedSignature2: (sourceSample.authorizedSignature2 as any)?._id || '' || undefined,
      });
    }
    // eslint-disable-next-line
  }, [copySampleData, isCopyMode, props.sampleDetails]);

  const promiseCustomerOptions = async (inputValue: string) => {
    const list = await props.searchCustomer(laboratoryId, { page: 1, size: 10, query: inputValue });

    return list.data.customers.map((i: any) => ({
      value: i._id,
      label: `${i.name} | (${i.address?.[0]?.line1 || ''}, ${i.address?.[0]?.line2 || ''}, ${i.address?.[0]?.city || ''
        }, ${i.address?.[0]?.state || ''})`,
      option: i,
    }));
  };

  const promiseReferenceOptions = async (inputValue: string) => {
    const list = await props.searchReference(laboratoryId, { page: 1, size: 10, query: inputValue });
    return list.data.references.map((i: any) => ({
      value: i._id,
      label: i.name,
      option: i,
    }));
  };

  const promiseTestGroupOptions = async (inputValue: string) => {
    const list = await props.searchTestGroupList(laboratoryId, { page: 1, size: 10, query: inputValue });
    return list.data.testGroups.map((i: any) => ({
      value: i._id,
      label: i.name,
      option: i,
    }));
  };

  useEffect(() => {
    props.searchCustomer(laboratoryId, { page: 1, size: 10, query: customerSearchQuery });
    // eslint-disable-next-line
  }, [customerSearchQuery]);

  useEffect(() => {
    props.searchMasterList(laboratoryId, { page: 1, size: 10, query: masterSearchQuery });
    // eslint-disable-next-line
  }, [masterSearchQuery]);

  useEffect(() => {
    props.searchTestGroupList(laboratoryId, { page: 1, size: 10, query: testGroupSearchQuery });
    // eslint-disable-next-line
  }, [testGroupSearchQuery]);

  useEffect(() => {
    props.searchReference(laboratoryId, { page: 1, size: 10, query: referenceSearchQuery });
    // eslint-disable-next-line
  }, [referenceSearchQuery]);

  useEffect(() => {
    if (!selectedCustomer && formik.values.customer) {
      const foundCustomer = props.searchCustomerList.find((item) => item._id === formik.values.customer);
      if (foundCustomer) setSelectedCustomer(foundCustomer);
    }
  }, [formik.values.customer, props.searchCustomerList, selectedCustomer]);

  const breadcrumbsList: IHeader['breadcrumbsList'] = [
    {
      label: 'Dashboard',
      onClick: () => navigate('/'),
    },
    {
      label: 'Samples',
      onClick: () => navigate('/laboratory/samples'),
    },
  ];
  const additionalFields: any = formik.values.additionalFields;

  const handleChange = (event: any) => {
    const { value } = event.target;
    const length = event.target.value.length;
    if (value[length - 1] === ' ' && value[length - 2] === ' ') {
      event.target.value = handleChangeWhiteSpace(value);
    }
    formik.handleChange(event);
  };

  const handleSelectAllParameter = () => {
    const parameters = selectedMaster?.parameters?.length ? selectedMaster.parameters : availableParameters;
    if (parameters?.length) {
      formik.setFieldValue('parameters', parameters);
      formik.setFieldError('parameters', undefined);
      return;
    }

    if (!formik.values.parameters.length) {
      formik.setFieldError('parameters', 'Please select a master');
    }
  };
  const gstAmount = formik.values.rate * (formik.values.gst / 100);
  const totalRate = formik.values.rate + gstAmount;
  const discountAmount = totalRate * (formik.values.discount / 100);
  const total = totalRate - discountAmount;
  const remainingTotal = total - formik.values.paidAmount;

  return (
    <Box>
      <Header breadcrumbsList={breadcrumbsList} title="Sample Entry" />
      <Box p={2} pt={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} gap={1}>
            <Grid item xs={12}>
              <CustomFieldWithLabel width={120} label="Booking Party">
                <AsyncSelect
                  loadOptions={promiseCustomerOptions}
                  value={
                    selectedCustomer
                      ? {
                        value: selectedCustomer._id,
                        label: buildCustomerLabel(selectedCustomer),
                      }
                      : null
                  }
                  defaultOptions={
                    props.searchCustomerList?.length
                      ? props.searchCustomerList.map((i) => ({
                        label: `${i.name} | (${i.address?.[0]?.line1 || ''}, ${i.address?.[0]?.line2 || ''})`,
                        value: i._id,
                      }))
                      : []
                  }
                  onChange={(newValue: any) => {
                    const findCustomer = props.searchCustomerList.find((i) => i._id === newValue?.value);
                    if (findCustomer) setSelectedCustomer(findCustomer);
                    formik.setFieldValue('customer', newValue?.value || '');
                  }}
                />
              </CustomFieldWithLabel>
              {formik.touched.customer && formik.errors.customer}
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Grid container spacing={1} mt={1}>
              <Grid item xs={12}>
                <Typography style={{ textTransform: 'uppercase' }} fontWeight={'500'} fontSize={14}>
                  Basic Details
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} gap={1}>
                <Grid item xs={12}>
                  {/* <CustomFieldWithLabel width={120} label="Reference by">
                    <AsyncSelect
                      loadOptions={promiseReferenceOptions}
                      value={{ value: selectedReference?._id, label: selectedReference?.name }}
                      defaultOptions={(props.referenceList || []).map((i) => ({
                        label: i.name,
                        value: i._id,
                      }))}
                      onChange={(newValue: any) => {
                        const findRef = (props.referenceList || []).find((i) => i._id === newValue?.value);
                        if (findRef) setSelectedReference(findRef);
                        formik.setFieldValue('reference', newValue?.value || '');
                      }}
                    />
                  </CustomFieldWithLabel>
                  {formik.touched.reference && formik.errors.reference} */}
                </Grid>
              </Grid>

              <Grid item xs={12} gap={1} style={{ display: 'flex' }}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Test Group">
                    <AsyncSelect
                      loadOptions={promiseTestGroupOptions}
                      value={{ value: selectedTestGroup?._id, label: selectedTestGroup?.name }}
                      defaultOptions={(props.testGroupsList || []).map((i) => ({
                        label: i.name,
                        value: i._id,
                      }))}
                      onChange={(newValue: any) => {
                        const testGroup = (props.testGroupsList || []).find((i) => i._id === newValue?.value);
                        if (testGroup) setSelectedTestGroup(testGroup);
                        formik.setFieldValue('testGroup', newValue?.value || '');
                      }}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Receipt date">
                    <LocalizationProvider dateAdapter={DateAdapter}>
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        // disabled={true}
                        onChange={(value) => {
                          formik.setFieldValue('collectionDate', normalizeDateForApi(value) || formik.values.collectionDate);
                          formik.setFieldTouched('collectionDate', true);
                        }}
                        value={formik.values.collectionDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="none"
                            error={formik.touched.collectionDate && formik.errors.collectionDate ? true : false}
                            helperText={formik.touched.collectionDate && formik.errors.collectionDate}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>

                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Sample Collection Date">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={(formik.values as any).sampleCollectionDate}
                      error={Boolean(
                        (formik.touched as any).sampleCollectionDate && (formik.errors as any).sampleCollectionDate
                      )}
                      helperText={
                        (formik.touched as any).sampleCollectionDate && (formik.errors as any).sampleCollectionDate
                      }
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        onChange={(value) => {
                          formik.setFieldValue('sampleCollectionDate', normalizeDateForApi(value));
                          formik.setFieldTouched('sampleCollectionDate', true);
                        }}
                        value={(formik.values as any).sampleCollectionDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="none"
                            error={Boolean(
                              (formik.touched as any).sampleCollectionDate &&
                              (formik.errors as any).sampleCollectionDate
                            )}
                            helperText={
                              (formik.touched as any).sampleCollectionDate &&
                              (formik.errors as any).sampleCollectionDate
                            }
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Due date">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={formik.values.dueDate}
                      error={formik.touched.dueDate && formik.errors.dueDate ? true : false}
                      helperText={formik.touched.dueDate && formik.errors.dueDate}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        onChange={(value) => {
                          formik.setFieldValue('dueDate', moment(value).toDate());
                          formik.setFieldTouched('dueDate', true);
                        }}
                        value={formik.values.dueDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            margin="none"
                            fullWidth
                            error={formik.touched.dueDate && formik.errors.dueDate ? true : false}
                            helperText={formik.touched.dueDate && formik.errors.dueDate}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>

                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Lab due Date">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={formik.values.labDueDate}
                      error={formik.touched.labDueDate && formik.errors.labDueDate ? true : false}
                      helperText={formik.touched.labDueDate && formik.errors.labDueDate}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        onChange={(value) => {
                          formik.setFieldValue('labDueDate', moment(value).toDate());
                          formik.setFieldTouched('labDueDate', true);
                        }}
                        value={formik.values.labDueDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="none"
                            error={formik.touched.labDueDate && formik.errors.labDueDate ? true : false}
                            helperText={formik.touched.labDueDate && formik.errors.labDueDate}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography style={{ textTransform: 'uppercase', marginTop: '1%' }} fontSize={14}>
                  Sample Details
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Sample Name">
                    <TextField
                      fullWidth
                      required
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setMasterSearchModal(true);
                        }
                      }}
                      value={formik.values.name || ''}
                      error={formik.touched.name && formik.errors.name ? true : false}
                      helperText={formik.touched.name && formik.errors.name}
                      name="name"
                      onChange={handleChange}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Generic Name">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      value={formik.values.genericName || ''}
                      error={formik.touched.genericName && formik.errors.genericName ? true : false}
                      helperText={formik.touched.genericName && formik.errors.genericName}
                      onChange={handleChange}
                      name={'genericName'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Sample Code">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.sampleCode || ''}
                      error={formik.touched.sampleCode && formik.errors.sampleCode ? true : false}
                      helperText={formik.touched.sampleCode && formik.errors.sampleCode}
                      name={'sampleCode'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Marking">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.marking || ''}
                      error={formik.touched.marking && formik.errors.marking ? true : false}
                      helperText={formik.touched.marking && formik.errors.marking}
                      name={'marking'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Brand Name">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.brandName || ''}
                      error={formik.touched.brandName && formik.errors.brandName ? true : false}
                      helperText={formik.touched.brandName && formik.errors.brandName}
                      name={'brandName'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Reference No">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      value={formik.values.refNo || ''}
                      onChange={handleChange}
                      error={formik.touched.refNo && formik.errors.refNo ? true : false}
                      helperText={formik.touched.refNo && formik.errors.refNo}
                      name={'refNo'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Manufacture Name">
                    <TextField
                      fullWidth
                      required
                      onChange={handleChange}
                      margin="none"
                      value={formik.values.manufacture || ''}
                      error={formik.touched.manufacture && formik.errors.manufacture ? true : false}
                      helperText={formik.touched.manufacture && formik.errors.manufacture}
                      name={'manufacture'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Importer/supplier & Industry Name">
                    <TextField
                      fullWidth
                      required
                      onChange={handleChange}
                      margin="none"
                      value={formik.values.nameAndAddressOfImporter || ''}
                      error={
                        formik.touched.nameAndAddressOfImporter && formik.errors.nameAndAddressOfImporter ? true : false
                      }
                      helperText={formik.touched.nameAndAddressOfImporter && formik.errors.refNo}
                      name={'nameAndAddressOfImporter'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Type">
                    <TextField
                      fullWidth
                      required
                      onChange={handleChange}
                      margin="none"
                      value={formik.values.type || ''}
                      error={formik.touched.type && formik.errors.type ? true : false}
                      helperText={formik.touched.type && formik.errors.type}
                      name={'type'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Drug Lic No">
                    <TextField
                      fullWidth
                      required
                      onChange={handleChange}
                      margin="none"
                      value={formik.values.drugLicNo || ''}
                      error={formik.touched.drugLicNo && formik.errors.drugLicNo ? true : false}
                      helperText={formik.touched.drugLicNo && formik.errors.drugLicNo}
                      name={'drugLicNo'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Serving Size">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.servingSize || ''}
                      error={formik.touched.servingSize && formik.errors.servingSize ? true : false}
                      helperText={formik.touched.servingSize && formik.errors.servingSize}
                      name={'servingSize'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Batch No">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.batchNo || ''}
                      error={formik.touched.batchNo && formik.errors.batchNo ? true : false}
                      helperText={formik.touched.batchNo && formik.errors.batchNo}
                      name={'batchNo'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Sample Location">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.sampleLocation || ''}
                      error={formik.touched.sampleLocation && formik.errors.sampleLocation ? true : false}
                      helperText={formik.touched.sampleLocation && formik.errors.sampleLocation}
                      name={'sampleLocation'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Mfg Date">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={formik.values.mfgDate}
                      error={formik.touched.mfgDate && formik.errors.mfgDate ? true : false}
                      helperText={formik.touched.mfgDate && formik.errors.mfgDate}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        onChange={(value) => {
                          const date = value && moment(value).isValid() ? moment(value).toDate() : null;
                          formik.setFieldValue('mfgDate', date);
                          formik.setFieldTouched('mfgDate', true);
                        }}
                        value={formik.values.mfgDate || null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="none"
                            error={formik.touched.mfgDate && formik.errors.mfgDate ? true : false}
                            helperText={formik.touched.mfgDate && formik.errors.mfgDate}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>

                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Best before buy (Exp Date)">
                    <LocalizationProvider dateAdapter={DateAdapter}
                      value={formik.values.expDate}
                      error={formik.touched.expDate && formik.errors.expDate ? true : false}
                      helperText={formik.touched.expDate && formik.errors.expDate}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        value={formik.values.expDate || null}
                        onChange={(value) => {
                          const date = value && moment(value).isValid() ? moment(value).toDate() : null;
                          formik.setFieldValue('expDate', date);
                          formik.setFieldTouched('expDate', true);
                        }}
                        renderInput={(params) => <TextField {...params} fullWidth margin="none" />}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Rec qty / Batch Size">
                    <TextField
                      fullWidth
                      required
                      onChange={handleChange}
                      margin="none"
                      value={formik.values.batchSize || ''}
                      error={formik.touched.batchSize && formik.errors.batchSize ? true : false}
                      helperText={formik.touched.batchSize && formik.errors.batchSize}
                      name={'batchSize'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Sample Qty">
                    <TextField
                      fullWidth
                      required
                      onChange={handleChange}
                      margin="none"
                      value={formik.values.sampleQty || ''}
                      error={formik.touched.sampleQty && formik.errors.sampleQty ? true : false}
                      helperText={formik.touched.sampleQty && formik.errors.sampleQty}
                      name={'sampleQty'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Sample Package">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.samplePacking || ''}
                      error={formik.touched.samplePacking && formik.errors.samplePacking ? true : false}
                      helperText={formik.touched.samplePacking && formik.errors.samplePacking}
                      name={'samplePacking'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Sample Condition">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.sampleCondition || ''}
                      error={formik.touched.sampleCondition && formik.errors.sampleCondition ? true : false}
                      helperText={formik.touched.sampleCondition && formik.errors.sampleCondition}
                      name={'sampleCondition'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Environmet Condition">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.envCondition || ''}
                      error={formik.touched.envCondition && formik.errors.envCondition ? true : false}
                      helperText={formik.touched.envCondition && formik.errors.envCondition}
                      name={'envCondition'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Sampling Method">
                    <TextField
                      fullWidth
                      required
                      onChange={handleChange}
                      margin="none"
                      value={formik.values.samplingMethod || ''}
                      error={formik.touched.samplingMethod && formik.errors.samplingMethod ? true : false}
                      helperText={formik.touched.samplingMethod && formik.errors.samplingMethod}
                      name={'samplingMethod'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Test Method">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.testMethod || ''}
                      error={formik.touched.testMethod && formik.errors.testMethod ? true : false}
                      helperText={formik.touched.testMethod && formik.errors.testMethod}
                      name={'testMethod'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Location of Analysis">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.locationOfAnalysis || ''}
                      error={formik.touched.locationOfAnalysis && formik.errors.locationOfAnalysis ? true : false}
                      helperText={formik.touched.locationOfAnalysis && formik.errors.locationOfAnalysis}
                      name={'locationOfAnalysis'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Source of Water">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.sourceOfWater || ''}
                      error={formik.touched.sourceOfWater && formik.errors.sourceOfWater ? true : false}
                      helperText={formik.touched.sourceOfWater && formik.errors.sourceOfWater}
                      name={'sourceOfWater'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Statement">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      onChange={handleChange}
                      value={formik.values.statement || ''}
                      error={formik.touched.statement && formik.errors.statement ? true : false}
                      helperText={formik.touched.statement && formik.errors.statement}
                      name={'statement'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              {selectedTestGroup?.additionalFields && selectedTestGroup?.additionalFields.length ? (
                <Grid item xs={12}>
                  <Typography style={{ textTransform: 'uppercase' }} fontWeight={'500'} fontSize={14}>
                    Additional Details
                  </Typography>
                  <Divider />
                </Grid>
              ) : null}

              {selectedTestGroup?.additionalFields &&
                (selectedTestGroup?.additionalFields || []).map((field) => {
                  return (
                    <Grid item xs={6}>
                      <CustomFieldWithLabel width={120} label={field.key}>
                        <TextField
                          fullWidth
                          // required
                          value={additionalFields[field.key]}
                          onChange={handleChange}
                          onBlur={formik.handleBlur}
                          // error={formik.touched.rate && formik.errors.rate ? true : false}
                          // helperText={formik.touched.rate && formik.errors.rate}
                          name={`additionalFields.${field.key}`}
                        />
                      </CustomFieldWithLabel>
                    </Grid>
                  );
                })}
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Grid container spacing={1} mt={0.5}>
              <Grid item xs={12}>
                <Typography style={{ textTransform: 'uppercase' }} fontWeight={'500'} fontSize={14}>
                  Details for laboratory use
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <CustomFieldWithLabel label="Declaration regarding Food Additives/if Any other" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.otherInformation}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.otherInformation && formik.errors.otherInformation ? true : false}
                    helperText={formik.touched.otherInformation && formik.errors.otherInformation}
                    name={'otherInformation'}
                  />
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={12}>
                <CustomFieldWithLabel label="Remark-I/Declared Values" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.remark1}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.remark1 && formik.errors.remark1 ? true : false}
                    helperText={formik.touched.remark1 && formik.errors.remark1}
                    name={'remark1'}
                  />
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={12}>
                <CustomFieldWithLabel label="Special Instructions" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.specialInstructions}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.specialInstructions && formik.errors.specialInstructions ? true : false}
                    helperText={formik.touched.specialInstructions && formik.errors.specialInstructions}
                    name={'specialInstructions'}
                  />
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={12}>
                <CustomFieldWithLabel label="Test Report as Per" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.testAsPer}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.testAsPer && formik.errors.testAsPer ? true : false}
                    helperText={formik.touched.testAsPer && formik.errors.testAsPer}
                    name={'testAsPer'}
                  />
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel width={110} label="Collection By">
                  {/* @ts-ignore: Unreachable code error */}
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.collectionBy}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.collectionBy && formik.errors.collectionBy ? true : false}
                    helperText={formik.touched.collectionBy && formik.errors.collectionBy}
                    name={'collectionBy'}
                  />
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel width={110} label="Receive Mode">
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    // @ts-ignore: Unreachable code error
                    value={formik.values.sampleReceivedMode}
                    error={formik.touched.sampleReceivedMode && formik.errors.sampleReceivedMode ? true : false}
                    helperText={formik.touched.sampleReceivedMode && formik.errors.sampleReceivedMode}
                  >
                    <Select
                      labelId="sampleReceivedMode-select-label"
                      id="sampleReceivedMode-select-helper"
                      name="sampleReceivedMode"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.sampleReceivedMode}
                      error={formik.touched.sampleReceivedMode && formik.errors.sampleReceivedMode ? true : false}
                    >
                      <MenuItem value={'Self'}>Self</MenuItem>
                      <MenuItem value={'Courier'}>Courier</MenuItem>
                      <MenuItem value={'Mail'}>Mail</MenuItem>
                    </Select>
                    <FormHelperText>
                      {formik.touched.sampleReceivedMode && formik.errors.sampleReceivedMode}
                    </FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel label="HOD" width={110}>
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    value={formik.values.hod}
                    error={formik.touched.hod && formik.errors.hod ? true : false}
                    helperText={formik.touched.hod && formik.errors.hod}
                  >
                    <Select
                      labelId="hod-select-label"
                      id="hod-select-helper"
                      name="hod"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.hod}
                      error={formik.touched.hod && formik.errors.hod ? true : false}
                    >
                      {props.userList
                        .filter((i) => i.role === 'ADMIN' || i.role === 'HOD')
                        .map((item) => (
                          <MenuItem value={item.user._id} key={item._id}>
                            {item.user.name}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>{formik.touched.hod && formik.errors.hod}</FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel label="Authorized Signature 1" width={110}>
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    value={formik.values.authorizedSignature1}
                    error={formik.touched.authorizedSignature1 && formik.errors.authorizedSignature1 ? true : false}
                    helperText={formik.touched.authorizedSignature1 && formik.errors.authorizedSignature1}
                  >
                    <Select
                      labelId="authorizedSignature1-select-label"
                      id="authorizedSignature1-select-helper"
                      name="authorizedSignature1"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.authorizedSignature1}
                      error={formik.touched.authorizedSignature1 && formik.errors.authorizedSignature1 ? true : false}
                    >
                      {props.userList
                        .filter((i) => i.role === 'ADMIN' || i.role === 'HOD')
                        .map((item) => (
                          <MenuItem value={item.user._id} key={item._id}>
                            {item.user.name}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                      {formik.touched.authorizedSignature1 && formik.errors.authorizedSignature1}
                    </FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Authorized Signature 2" width={110}>
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    value={formik.values.authorizedSignature2}
                    error={formik.touched.authorizedSignature2 && formik.errors.authorizedSignature2 ? true : false}
                    helperText={formik.touched.authorizedSignature2 && formik.errors.authorizedSignature2}
                  >
                    <Select
                      labelId="authorizedSignature2-select-label"
                      id="authorizedSignature2-select-helper"
                      name="authorizedSignature2"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.authorizedSignature2}
                      error={formik.touched.authorizedSignature2 && formik.errors.authorizedSignature2 ? true : false}
                    >
                      {props.userList
                        .filter((i) => i.role === 'ADMIN' || i.role === 'HOD')
                        .map((item) => (
                          <MenuItem value={item.user._id} key={item._id}>
                            {item.user.name}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                      {formik.touched.authorizedSignature2 && formik.errors.authorizedSignature2}
                    </FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel label="Reviewed By" width={110}>
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    value={formik.values.reviewedBy}
                    error={formik.touched.reviewedBy && formik.errors.reviewedBy ? true : false}
                    helperText={formik.touched.reviewedBy && formik.errors.reviewedBy}
                  >
                    <Select
                      labelId="reviewedBy-select-label"
                      id="reviewedBy-select-helper"
                      name="reviewedBy"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.reviewedBy}
                      error={formik.touched.reviewedBy && formik.errors.reviewedBy ? true : false}
                    >
                      {props.userList.map((item) => (
                        <MenuItem value={item.user._id} key={item._id}>
                          {item.user.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{formik.touched.reviewedBy && formik.errors.reviewedBy}</FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={12}>
                <Typography style={{ textTransform: 'uppercase' }} fontWeight={'500'} fontSize={14}>
                  Billing Details
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label={'Charges (Rate)'}
                  type="number"
                  value={formik.values.rate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.rate && formik.errors.rate ? true : false}
                  helperText={formik.touched.rate && formik.errors.rate}
                  name={'rate'}
                />
                <TextField
                  fullWidth
                  label={'Discount %'}
                  value={formik.values.discount}
                  type="number"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.discount && formik.errors.discount ? true : false}
                  helperText={formik.touched.discount && formik.errors.discount}
                  name={'discount'}
                />
                <Box display={'flex'} gap={1}>
                  <TextField
                    fullWidth
                    type="number"
                    style={{ flex: 0.5 }}
                    label={'GST %'}
                    value={formik.values.gst}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.gst && formik.errors.gst ? true : false}
                    helperText={formik.touched.gst && formik.errors.gst}
                    name={'gst'}
                  />
                  <Box display={'flex'} style={{ flex: 0.5 }} alignItems={'center'}>
                    <FormControl margin="dense" fullWidth size="small" required>
                      {/* <InputLabel id="department-select-label">Department</InputLabel> */}
                      <Select
                        id="demo-simple-select-helper"
                        name="igst"
                        onChange={(e) => {
                          formik.setFieldValue('igst', e.target.value === 'true');
                        }}
                        value={formik.values.igst ? 'true' : 'false'}
                      >
                        <MenuItem value={'false'}>CGST / SGST</MenuItem>
                        <MenuItem value={'true'}>IGST</MenuItem>
                      </Select>
                    </FormControl>
                    {/* Render checkbox for igst formik field */}
                    {/* <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.igst}
                      onChange={formik.handleChange}
                      name="igst"
                      color="primary"
                      size="small"
                    />
                  }
                  label="IGST"
                /> */}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={'Paid Amount'}
                  value={formik.values.paidAmount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.paidAmount && formik.errors.paidAmount ? true : false}
                  helperText={formik.touched.paidAmount && formik.errors.paidAmount}
                  name={'paidAmount'}
                />
                <TextField fullWidth disabled label={'Total'} value={total} />
                <TextField fullWidth disabled label={'Remaining Total'} value={remainingTotal} />
              </Grid>

              <Grid item xs={4}>
                <LocalizationProvider dateAdapter={DateAdapter}>
                  <DesktopDatePicker
                    label="Payment Due Date"
                    inputFormat="DD/MM/YYYY"
                    onChange={(value) => {
                      formik.setFieldValue('paymentDueDate', moment(value).toDate());
                      formik.setFieldTouched('paymentDueDate', true);
                    }}
                    value={formik.values.paymentDueDate}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={formik.touched.paymentDueDate && formik.errors.paymentDueDate ? true : false}
                        helperText={formik.touched.paymentDueDate && formik.errors.paymentDueDate}
                      />
                    )}
                  />
                </LocalizationProvider>
                <TextField
                  fullWidth
                  label={'SAC Code'}
                  value={formik.values.sac}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.sac && formik.errors.sac ? true : false}
                  helperText={formik.touched.sac && formik.errors.sac}
                  name={'sac'}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Box display={'flex'} gap={2}>
              <Box flex={1}>
                <CustomFieldWithLabel width={120} label="Parameters">
                  {/* @ts-ignore */}
                  <ReactSelect
                    isMulti
                    closeMenuOnSelect={false}
                    components={animatedComponents}
                    value={(formik.values.parameters || []).map((i: any) => ({ value: i.name, label: i.name }))}
                    onChange={(value) => {
                      const selectedOptions = Array.isArray(value) ? value : [];
                      const sourceParameters = selectedMaster?.parameters?.length
                        ? selectedMaster.parameters
                        : availableParameters.length
                          ? availableParameters
                          : formik.values.parameters || [];
                      const selectedParameters = sourceParameters.filter((i: any) =>
                        selectedOptions.map((option: any) => option.value).includes(i.name)
                      );
                      formik.setFieldValue('parameters', selectedParameters);
                    }}
                    options={(selectedMaster?.parameters?.length
                      ? selectedMaster.parameters
                      : availableParameters.length
                        ? availableParameters
                        : formik.values.parameters || []
                    ).map((i: any) => ({
                      value: i.name,
                      label: i.name,
                    }))}
                  />
                </CustomFieldWithLabel>
                <div>{formik.errors.parameters && <div style={{ color: 'red' }}>{formik.errors.parameters}</div>}</div>
              </Box>
              <div>
                <Button variant="contained" onClick={handleSelectAllParameter}>
                  Select All Parameters
                </Button>
              </div>
            </Box>
          </Grid>
        </Grid>

        <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} mt={2}>
          <Box>
            <Button disabled={formik.isSubmitting} variant="outlined" onClick={() => navigate('/laboratory/samples')}>
              Back
            </Button>
          </Box>
          <Box ml={1}>
            <Button variant="contained" disabled={formik.isSubmitting} onClick={() => formik.handleSubmit()}>
              {sampleId ? 'Update' : isCopyMode ? 'Create' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Box>

      <SearchDialog
        isVisible={customerSearchModal}
        title={'Search customer'}
        body={
          <Box>
            <TextField
              fullWidth
              label={'Search'}
              value={customerSearchQuery}
              autoFocus
              onChange={(e) => setCustomerSearchQuery(e.target.value)}
            />
            {!props.searchCustomerList.length && (
              <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <Typography variant="subtitle1" fontSize={14} pl={1}>
                  Not found
                </Typography>
              </Box>
            )}
            {props.searchCustomerList.map((c) => (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedCustomer(c);
                  setCustomerSearchModal(false);
                  setCustomerSearchQuery('');
                  formik.setFieldValue('customer', c._id);
                }}
              >
                <Box display={'flex'} pt={1} pb={1} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography variant="h6" color={'#000'} fontSize={14}>
                    {c.name}
                  </Typography>
                  <Typography variant="subtitle1" color={'#000'} fontSize={14} pl={1}>
                    {c.email}
                  </Typography>
                  <Typography variant="subtitle1" color={'#000'} fontSize={14} pl={1}>
                    {c.country} {c.phone}
                  </Typography>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
        }
        onAction={() => { }}
        onClose={() => {
          setCustomerSearchModal(false);
          setCustomerSearchQuery('');
        }}
        hideButtons={true}
      />

      <SearchDialog
        isVisible={testGroupSearchModal}
        title={'Search Test Group'}
        body={
          <Box>
            <TextField
              fullWidth
              label={'Search'}
              value={testGroupSearchQuery}
              autoFocus
              onChange={(e) => setTestGroupSearchQuery(e.target.value)}
            />
            {!props.masterList.length && (
              <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <Typography variant="subtitle1" fontSize={14} pl={1}>
                  Not found
                </Typography>
              </Box>
            )}
            {props.testGroupsList.map((m) => (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedTestGroup(m);
                  setTestGroupSearchModal(false);
                  setTestGroupSearchQuery('');
                  formik.setFieldValue('testGroup', m._id);
                }}
              >
                <Box display={'flex'} pt={1} pb={1} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography variant="h6" color={'#000'} fontSize={14}>
                    {m.name}
                  </Typography>
                  <Typography variant="subtitle1" color={'#000'} fontSize={14} pl={1}>
                    {m.prefix}
                  </Typography>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
        }
        onAction={() => { }}
        onClose={() => {
          setTestGroupSearchModal(false);
          setTestGroupSearchQuery('');
        }}
        hideButtons={true}
      />

      <SearchDialog
        isVisible={masterSearchModal}
        title={'Search Master'}
        body={
          <Box>
            <TextField
              fullWidth
              label={'Search'}
              value={masterSearchQuery}
              autoFocus
              onChange={(e) => setMasterSearchQuery(e.target.value)}
            />
            {!props.masterList.length && (
              <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <Typography variant="subtitle1" fontSize={14} pl={1}>
                  Not found
                </Typography>
              </Box>
            )}
            {props.masterList.map((m) => (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedMaster(m);
                  setAvailableParameters(m.parameters || []);
                  setMasterSearchModal(false);
                  setMasterSearchQuery('');
                  formik.setFieldValue('name', m.sampleName);
                  formik.setFieldValue('marking', m.marking);
                  formik.setFieldValue('name', m.sampleName);
                  formik.setFieldValue('genericName', m.genericName);
                  formik.setFieldValue('testMethod', m.testingMethod);
                  formik.setFieldValue('parameters', []);
                }}
              >
                <Box display={'flex'} pt={1} pb={1} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography variant="h6" color={'#000'} fontSize={14}>
                    {m.sampleName}
                  </Typography>
                  <Typography variant="subtitle1" color={'#000'} fontSize={14} pl={1}>
                    {m.genericName}
                  </Typography>
                  <Typography variant="subtitle1" color={'#000'} fontSize={14} pl={1}>
                    {m.masterId}
                  </Typography>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
        }
        onAction={() => { }}
        onClose={() => {
          setMasterSearchModal(false);
          setMasterSearchQuery('');
        }}
        hideButtons={true}
      />
      <SearchDialog
        isVisible={referenceSearchModal}
        title={'Search Reference'}
        body={
          <Box>
            <TextField
              fullWidth
              label={'Search'}
              value={referenceSearchQuery}
              autoFocus
              onChange={(e) => setReferenceSearchQuery(e.target.value)}
            />
            {!props?.referenceList?.length && (
              <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <Typography variant="subtitle1" fontSize={14} pl={1}>
                  Not found
                </Typography>
              </Box>
            )}
            {props?.referenceList?.map((m) => (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedReference(m);
                  setReferenceSearchModal(false);
                  setReferenceSearchQuery('');
                  formik.setFieldValue('reference', m._id);
                }}
              >
                <Box display={'flex'} pt={1} pb={1} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography variant="h6" color={'#000'} fontSize={14}>
                    {m.name}
                  </Typography>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
        }
        onAction={() => { }}
        onClose={() => {
          setReferenceSearchModal(false);
          setReferenceSearchQuery('');
        }}
        hideButtons={true}
      />
    </Box>
  );
}

const mapStateToProps = (state: IReducer) => ({
  user: state.auth.user,
  laboratory: state.laboratory.current,
  userList: Array.isArray(state.laboratory.users)
    ? state.laboratory.users.filter((item) => item?.user?._id)
    : [],
  department: Array.isArray(state.department.list) ? state.department.list : [],
  searchCustomerList: Array.isArray(state.laboratory.searchCustomer) ? state.laboratory.searchCustomer : [],
  masterList: Array.isArray(state.laboratory.masters) ? state.laboratory.masters : [],
  testGroupsList: Array.isArray(state.laboratory.testGroups) ? state.laboratory.testGroups : [],
  referenceList: Array.isArray(state.laboratory.reference) ? state.laboratory.reference : [],
  sampleDetails: state.laboratory.sampleDetails
    ? {
        ...state.laboratory.sampleDetails,
        parameters: Array.isArray(state.laboratory.sampleDetails.parameters)
          ? state.laboratory.sampleDetails.parameters
          : [],
      }
    : null,
});

function mapDispatchToProps(dispatch: any) {
  return {
    ...bindActionCreators(
      {
        searchCustomer,
        searchMasterList,
        searchTestGroupList,
        getUserList,
        createSample,
        searchReference,
        getSampleById,
        updateSample,
        copySample,
      },
      dispatch
    ),
  };
}

export const PersistSampleFoodAndWater = connect(mapStateToProps, mapDispatchToProps)(_PersistSampleFoodAndWater);
